
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Region, RegionFormData } from '@/types/regions';
import { toast } from 'sonner';

export const useRegions = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRegions = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('regions')
        .select('*')
        .order('name');

      if (error) throw error;
      setRegions(data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Failed to fetch regions');
    } finally {
      setIsLoading(false);
    }
  };

  const createRegion = async (regionData: RegionFormData) => {
    try {
      const { data, error } = await supabase
        .from('regions')
        .insert(regionData)
        .select()
        .single();

      if (error) throw error;
      
      setRegions(prev => [...prev, data]);
      toast.success('Region created successfully');
      return data;
    } catch (error) {
      console.error('Error creating region:', error);
      toast.error('Failed to create region');
      throw error;
    }
  };

  const updateRegion = async (id: string, regionData: Partial<RegionFormData>) => {
    try {
      const { data, error } = await supabase
        .from('regions')
        .update(regionData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setRegions(prev => prev.map(region => 
        region.id === id ? data : region
      ));
      toast.success('Region updated successfully');
      return data;
    } catch (error) {
      console.error('Error updating region:', error);
      toast.error('Failed to update region');
      throw error;
    }
  };

  const deleteRegion = async (id: string) => {
    try {
      const { error } = await supabase
        .from('regions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setRegions(prev => prev.filter(region => region.id !== id));
      toast.success('Region deleted successfully');
    } catch (error) {
      console.error('Error deleting region:', error);
      toast.error('Failed to delete region');
      throw error;
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  return {
    regions,
    isLoading,
    fetchRegions,
    createRegion,
    updateRegion,
    deleteRegion,
  };
};
